import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { 
  Search, 
  Plus, 
  X, 
  Loader2, 
  AlertCircle,
  CheckCircle,
  FileText,
  FileImage
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { prechartingService } from '../../../apis/prechartingService';
import OCRModal from './ocr-modal';

const ProblemListForm = ({ data, onChange, onSave, isLoading, readonly = false }) => {
  const [problems, setProblems] = useState(data || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showOCRModal, setShowOCRModal] = useState(false);
  const searchTimeoutRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (data) {
      setProblems(data);
    }
  }, [data]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performSearch = async (query) => {
    try {
      setIsSearching(true);
      setShowResults(true);
      
      const response = await prechartingService.searchICD10Codes(query, 10);
      
      if (response.success && response.data) {
        setSearchResults(response.data);
        setSelectedIndex(-1);
      } else {
        setSearchResults([]);
        if (response.message && !response.message.includes('not found')) {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.error('Error searching ICD-10 codes:', error);
      setSearchResults([]);
      toast.error('Failed to search ICD-10 codes');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProblem = (code, description) => {
    const exists = problems.some(p => p.code === code);
    if (exists) {
      toast.error('This code is already in the problem list');
      return;
    }

    const newProblem = {
      code: code,
      description: description || '',
      id: Date.now().toString()
    };

    const updatedProblems = [...problems, newProblem];
    setProblems(updatedProblems);
    
    if (onChange) {
      onChange(updatedProblems);
    }

    // Clear search
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    
    toast.success('Code added to problem list');
  };

  const handleRemoveProblem = (index) => {
    const updatedProblems = problems.filter((_, i) => i !== index);
    setProblems(updatedProblems);
    
    if (onChange) {
      onChange(updatedProblems);
    }

    toast.success('Code removed from problem list');
  };

  const handleUpdateDescription = (index, description) => {
    const updatedProblems = [...problems];
    updatedProblems[index].description = description;
    setProblems(updatedProblems);
    
    if (onChange) {
      onChange(updatedProblems);
    }
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          const result = searchResults[selectedIndex];
          handleAddProblem(result.code, result.description);
        }
        break;
      case 'Escape':
        setShowResults(false);
        break;
    }
  };

  const hasData = () => {
    return problems.length > 0;
  };

  const handleSaveClick = async () => {
    if (!hasData()) {
      toast.error('Please add at least one ICD-10 code before saving');
      return;
    }

    if (onSave) {
      await onSave(problems);
    }
  };

  const handleOCRComplete = useCallback((extractedCodes) => {
    const newProblems = [];
    const existingCodes = new Set(problems.map(p => p.code));

    for (const code of extractedCodes) {
      if (!existingCodes.has(code.code)) {
        newProblems.push({
          code: code.code,
          description: code.description || '',
          id: Date.now().toString() + Math.random()
        });
        existingCodes.add(code.code);
      }
    }

    if (newProblems.length > 0) {
      const updatedProblems = [...problems, ...newProblems];
      setProblems(updatedProblems);
      
      if (onChange) {
        onChange(updatedProblems);
      }

      toast.success(`Added ${newProblems.length} codes from OCR extraction`);
    } else {
      toast.info('All extracted codes already exist in the problem list');
    }
  }, [problems, onChange]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Problem List - ICD-10-CM Codes
            </CardTitle>
            {!readonly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOCRModal(true)}
                className="flex items-center gap-2"
              >
                <FileImage className="h-4 w-4" />
                Extract from Image
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Enter ICD-10-CM codes that the patient already has. The bot should recognize these codes in SOAP note generation.
            </AlertDescription>
          </Alert>

          {!readonly && (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search ICD-10-CM codes (e.g., E11.9, diabetes, hypertension)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="pl-10"
                  disabled={readonly}
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div 
                  ref={resultsRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                >
                  {searchResults.map((result, index) => (
                    <div
                      key={result.code}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        index === selectedIndex ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleAddProblem(result.code, result.description)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="font-mono">
                              {result.code}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {result.description}
                            </span>
                          </div>
                        </div>
                        <Plus className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* No Results */}
              {showResults && !isSearching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4">
                  <p className="text-sm text-gray-500 text-center">
                    No ICD-10-CM codes found. Try a different search term.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Problem List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Current Problem List</h3>
              <Badge variant="secondary">{problems.length} {problems.length === 1 ? 'code' : 'codes'}</Badge>
            </div>

            {problems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No ICD-10-CM codes added yet</p>
                <p className="text-xs text-gray-400 mt-1">Search and add codes above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {problems.map((problem, index) => (
                  <Card key={problem.id || index} className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                          <Badge variant="outline" className="font-mono">
                            {problem.code}
                          </Badge>
                          {readonly ? (
                            <span className="text-sm text-gray-700 flex-1">
                              {problem.description || 'No description'}
                            </span>
                          ) : (
                            <Input
                              type="text"
                              placeholder="Description of code..."
                              value={problem.description || ''}
                              onChange={(e) => handleUpdateDescription(index, e.target.value)}
                              className="w-full sm:flex-1"
                            />
                          )}
                        </div>
                      </div>
                      {!readonly && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveProblem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {!readonly && onSave && (
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSaveClick}
                disabled={isLoading || !hasData()}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Problem List
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <OCRModal
        isOpen={showOCRModal}
        onClose={() => setShowOCRModal(false)}
        onExtractComplete={handleOCRComplete}
        setShowOCRModal={setShowOCRModal}
      />
    </div>
  );
};

export default ProblemListForm;
